var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { Fragment, memo, useMemo, useRef, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton/index';
import { ReactPortal } from '../ReactPortal';
import { useLockBodyScrollEffect } from '../useLockBodyScrollEffect';
import { drawerGapHeight, drawerGapWidth, drawerWidth, positions, zIndexDrawer, zIndexOverlay, } from './constants';
import { DrawerContext, } from './DrawerContext';
import { DrawerTransition } from './DrawerTransition';
import { OverlayTransition } from './OverlayTransition';
import { overlayBackground } from './styles';
/* stylelint-disable unit-whitelist */
const overlayStyles = css `
  ${styleUtils.borderBox}
  ${overlayBackground}
  ${'' /* Will produce an iOS style background blur on supported devices.
px is used here as it is more appropriate to dialing in the blur */}
  backdrop-filter: blur(2px); /* stylelint-disable unit-allowed-list */
  height: 100%;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: ${zIndexOverlay};
`;
/* stylelint-enable unit-whitelist */
const drawerMaxHeightStyles = css `max-height: calc(100% - ${styleUtils.rem(drawerGapHeight)});`;
const drawerPositionStyles = {
    bottom: css `
    bottom: 0;
    left: 0;
    width: 100%;
    ${drawerMaxHeightStyles}
  `,
    left: css `
    left: 0;
    top: 0;
    height: 100%;
    width: ${drawerWidth};
    ${theme.media.lessThan('small') `
      width: calc(100% - ${drawerGapWidth});
    `}
  `,
    right: css `
    right: 0;
    top: 0;
    height: 100%;
    width: ${drawerWidth};
    ${theme.media.lessThan('small')(css `
      width: calc(100% - ${drawerGapWidth});
    `)}
  `,
    top: css `
    top: 0;
    left: 0;
    width: 100%;
    ${drawerMaxHeightStyles}
  `,
};
// this intentionally only applies the styling to small breakpoints for now,
// the intention is the eventually apply fullscreen styles to other breakpoints as well
const fullscreenStyles = css `
  ${theme.media.lessThan('small') `
    height: 100%;
    max-height: 100%;
    max-width: 100%;
    width: 100%;
  `}
`;
const headingStyles = css `
  display: flex;
  column-gap: ${theme.spacing.base};
  padding-bottom: ${theme.spacing.x4};
  padding-top: ${theme.spacing.x4};
`;
const titleStyles = css `
  ${theme.typeStyles.titleLargeStyles}
  padding-top: calc(${theme.spacing.base} / 2);
  width: 100%;
`;
const bodyStyles = css `
  ${theme.typeStyles.bodySmallStyles}
`;
const defaultProps = {
    className: '',
    collapse: false,
    fullscreen: false,
    isOverlayIconVisible: false,
    offsetCloseButton: false,
    onBackClick: undefined,
    onClose: undefined,
    onOverlayClick: undefined,
    position: positions.right,
    show: false,
};
const Drawer = (_a) => {
    var { children, className = defaultProps.className, contentPadding, isOverlayIconVisible = defaultProps.isOverlayIconVisible, mobileFullscreen, offsetCloseButton = defaultProps.offsetCloseButton, onBackClick = defaultProps.onBackClick, onClose = defaultProps.onClose, onOverlayClick = defaultProps.onOverlayClick, position = defaultProps.position, show = defaultProps.show, title } = _a, props = __rest(_a, ["children", "className", "contentPadding", "isOverlayIconVisible", "mobileFullscreen", "offsetCloseButton", "onBackClick", "onClose", "onOverlayClick", "position", "show", "title"]);
    const drawerContentRef = useRef(null);
    const drawerProviderValue = useMemo(() => ({
        drawerContentRef,
    }), [
        drawerContentRef,
    ]);
    const closeButtonContainerStyles = useMemo(() => (css `
        ${(offsetCloseButton && !title) && css `
          padding-bottom: 0;
          padding-left: 0;
          padding-right: ${theme.spacing.x3};
          padding-top: ${theme.spacing.x3};
        `}
      `), [
        offsetCloseButton,
        title,
    ]);
    const iconContainerStyles = useMemo(() => (css `
        color: ${theme.colors.gray100};
        display: flex;
        padding-bottom: ${theme.spacing.x4};
        padding-left: ${theme.spacing.x3};
        padding-right: ${theme.spacing.x3};
        padding-top: ${theme.spacing.x8};
        position: absolute;

        ${position === 'left' && css `
          right: 0;

          ${theme.media.greaterThan('small')(css `
            right: unset;
            left: ${drawerWidth};
          `)}
        `}

        ${position === 'right' && css `
          left: 0;

          ${theme.media.greaterThan('small')(css `
            left: unset;
            right: ${drawerWidth};
          `)}
        `}
      `), [
        position,
    ]);
    let { collapse, fullscreen, } = props;
    if (contentPadding !== undefined) {
        collapse = !contentPadding;
    }
    if (mobileFullscreen !== undefined) {
        fullscreen = mobileFullscreen;
    }
    const drawerStyles = useMemo(() => (css `
        ${theme.typeStyles.bodySmallStyles}
        ${styleUtils.borderBox}
        background: ${theme.colors.gray100};
        box-shadow: 0 0 ${styleUtils.rem(3)} 0 ${theme.colors.gray600};
        position: fixed;
        overflow-wrap: break-word;
        overflow-y: auto;
        z-index: ${zIndexDrawer};
        ${drawerPositionStyles[position]}
        ${fullscreen && fullscreenStyles}
        ${(!fullscreen && position === positions.bottom) && css `
          border-radius: ${styleUtils.rem(30)} ${styleUtils.rem(30)} 0 0;
        `}
        ${theme.media.greaterThan('small')(css `
          border-radius: 0;
        `)}
        ${(title || !collapse) && css `
          padding-bottom: ${theme.spacing.x8};
          padding-left: ${theme.spacing.x4};
          padding-right: ${theme.spacing.x4};
          padding-top: ${theme.spacing.x4};

          ${position === 'bottom' && theme.media.greaterThan('small')(css `
            padding-bottom: ${theme.spacing.x4};
          `)}
        `}
      `), [
        collapse,
        fullscreen,
        position,
        title,
    ]);
    useLockBodyScrollEffect({
        isLocked: show,
    });
    return (_jsx(ReactPortal, { children: _jsxs(Fragment, { children: [onOverlayClick
                    && (_jsx(OverlayTransition, Object.assign({ in: show }, { children: _jsx("div", Object.assign({ "aria-label": "Close", css: overlayStyles, onClick: onOverlayClick, role: "button", tabIndex: -1 }, { children: (isOverlayIconVisible
                                && (position === 'left'
                                    || position === 'right')
                                && (_jsx("div", Object.assign({ css: iconContainerStyles }, { children: _jsx(Icon, { size: "medium", type: "close" }) })))) })) }))), _jsx(DrawerTransition, Object.assign({ in: show, position: position }, { children: _jsxs("section", Object.assign({ className: className, css: drawerStyles, onClick: (event) => {
                            event.stopPropagation();
                        }, ref: drawerContentRef, role: "dialog" }, { children: [(onClose
                                || title)
                                && (_jsxs("div", Object.assign({ css: headingStyles }, { children: [Boolean(onBackClick)
                                            && (_jsx("div", Object.assign({ css: closeButtonContainerStyles }, { children: _jsx(IconButton, { iconType: "arrow-left", onClick: onBackClick, size: "large", type: "secondary" }) }))), title
                                            && (_jsx("div", Object.assign({ css: titleStyles }, { children: title }))), onClose
                                            && !onBackClick
                                            && (_jsx("div", Object.assign({ css: closeButtonContainerStyles }, { children: _jsx(IconButton, { iconType: "close", onClick: onClose, size: "large", type: "secondary" }) })))] }))), _jsx("div", Object.assign({ css: bodyStyles }, { children: _jsx(DrawerContext.Provider, Object.assign({ value: drawerProviderValue }, { children: children })) }))] })) }))] }) }));
};
const MemoizedDrawer = memo(Drawer);
export { MemoizedDrawer as Drawer };
// MC-1068 Temporary hack for Storybook.
export default Drawer;
